import { differenceInCalendarDays } from 'date-fns';
import { ClosetContextValue } from '@/components/context/ClosetContext/ClosetContext';
import { CLOSET_SLOT_TIER_SLUG } from '@/types/Closet';
import { User } from '@/types/User';
import { ProductDetailData } from '@/types/Product';
import { isPriveeMember } from '@/util/user_client';

type SwapDateData = {
  remainingDays: number;
  canSwapDate: Date;
};

/**
 * Retrieves the date of the soonest a user can swap out an item
 * @param slug A closet slot tier slug to check when the next swap date is
 * @param closet The user's closet value from the useCloset() hook
 */
export function getSwapDateForTierInCloset(
  slug: CLOSET_SLOT_TIER_SLUG,
  closetValue: ClosetContextValue
): SwapDateData | void {
  const { closet } = closetValue;
  if (closet === null) {
    return;
  }

  // Privee items live in closet.privee, not in closet.slots
  if (slug === CLOSET_SLOT_TIER_SLUG.PRIVEE) {
    const priveeItem = closet.privee?.currentItem;
    if (!priveeItem?.eligibleForSwapDate) {
      return;
    }
    const now = new Date();
    const swapDate = new Date(priveeItem.eligibleForSwapDate);
    return {
      canSwapDate: swapDate,
      remainingDays: differenceInCalendarDays(swapDate, now)
    };
  }

  const targetSlot = closet.slots.find((slot) => {
    if (slot.isOpen === false) {
      return slot.maxTier === slug;
    }
    return false;
  });

  if (!targetSlot) {
    return;
  }

  if (targetSlot.currentItem === null) {
    return;
  }

  const currentItem = targetSlot.currentItem;
  const { eligibleForSwapDate } = currentItem;

  if (!eligibleForSwapDate) {
    return;
  }

  const now = new Date();
  const swapDate = new Date(eligibleForSwapDate);

  const remainingDays = differenceInCalendarDays(swapDate, now);

  return {
    canSwapDate: swapDate,
    remainingDays
  };
}

/**
 * Determines if the PDP should render in the Privee variant.
 * Requires BOTH: user has active Privee status AND product is a Privee-tier item.
 */
export function isPriveePDP(
  user: User | undefined,
  productData: ProductDetailData
): boolean {
  if (!isPriveeMember(user)) return false;
  return productData.productTier.toLowerCase() === 'privee';
}
